// Preview/live switch for the Business section.
//
// The `business_accounting` feature flag gates the live accounting API:
//   flag OFF (default) → 'preview' — the existing sample-data experience.
//   flag ON            → 'live'    — real /api/v1/business/* endpoints.
//
// BusinessShell resolves the flag once per mount and provides the mode;
// components read it with useBusinessDataMode(). Outside a provider the mode
// is 'preview' (safe default — never hits the live API by accident).

import { createContext, useContext, useEffect, useState } from 'react';
import { featureFlag } from '../services/featureFlagClient';

export const BUSINESS_ACCOUNTING_FLAG = 'business_accounting';

export type BusinessDataMode = 'preview' | 'live';

const BusinessModeContext = createContext<BusinessDataMode>('preview');

export const BusinessModeProvider = BusinessModeContext.Provider;

export function useBusinessDataMode(): BusinessDataMode {
  return useContext(BusinessModeContext);
}

/**
 * Resolve the business_accounting flag. Returns null while resolving so the
 * shell can hold rendering (avoids flashing the preview at live users).
 * Any fetch failure resolves to false — preview is always the fallback.
 */
export function useBusinessAccountingFlag(): boolean | null {
  const [enabled, setEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    featureFlag
      .isEnabledAsync(BUSINESS_ACCOUNTING_FLAG)
      .then(value => {
        if (!cancelled) setEnabled(value);
      })
      .catch(() => {
        if (!cancelled) setEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return enabled;
}
